/* ============================================================
   chartTheme.js — Shared ECharts config for FOX dashboards
   Import this in every dashboard that uses ReactECharts.
   ============================================================ */

import { isDark as themeIsDark } from './theme';

const CHART_COLORS_LIGHT = {
  blue:  '#378ADD',
  green: '#3fb950',
  red:   '#f85149',
  amber: '#d29922',
  bgfc:  '#e07b23',
  nsa:   '#6a3d9a',
  // PFC's blue is the same on every page it appears on, which is the point of
  // it. Note for anyone tuning charts that put this next to NSA's purple: the
  // pair measures dE 14.8 for normal vision and 7.4 under deuteranopia, so the
  // two are close, and a chart drawing both needs something besides colour to
  // tell them apart -- Maintenance Throughput gives each line its own marker
  // shape and labels every series at its endpoint for that reason.
  pfc:   '#1a6fbd',
  // Extras
  purple: '#8b5cf6',
  // Human-Induced on the Lifecycles cause breakdown. Two earlier attempts sat in
  // the blue-violet family and both read as too close to the scheduled blue:
  // #8b5cf6 measured dE 14.6 in normal vision, #5b21b6 measured 23.7 and was
  // still rejected by eye. The lesson was that hue family beats the number --
  // a violet is a blue-dominant hue however dark it is stepped.
  //
  // Magenta leaves that family entirely: no blue dominance, reads pink rather
  // than purple. dE 16.8 (deutan) and 26.8 (normal) from the blue, and across
  // all four colours the Lifecycles page now carries -- blue, amber, magenta and
  // the trend chart's rust -- it is not the weakest pair. That is amber against
  // rust at 16.3.
  magenta: '#86198f',
  // "At overhaul (late)" on the Lifecycles coverage badges and chart, beside
  // magenta's "At overhaul". A second step of one hue rather than a new hue
  // because the coverage palette already holds six: green, blue, amber, gold,
  // red and grey, with the warm end spoken for by the two risk states. Checked
  // against the whole set with the palette validator -- its weakest pair is
  // 17.3 dE, which is the pre-existing red/amber pairing rather than anything
  // this colour introduces, and it clears #86198f itself by more than that.
  magentaLight: '#d946ef',
  teal:   '#2dd4bf',
  // The fourth phase hue on Aircraft Status. Mirrors --cyan in index.css, which
  // is where the same colour is defined for the chrome.
  cyan:   '#0e7490',
  // Second series on the engine replacement trend, for the average beside the
  // median. Picked with the palette validator rather than by eye: teal failed
  // the lightness band, a lighter blue failed the chroma floor, and a darker
  // teal failed the normal-vision floor at dE 14.6. This clears every check --
  // dE 31.7 against blue, and 20.2 against amber, so the two warm hues cannot
  // be confused across cards.
  rust:   '#c2410c',
};

/* ------------------------------------------------------------------
   Dark-theme overrides for the palette above.

   Only the colours that fail on a dark surface are listed; anything absent
   is identical in both themes and is inherited by chartColors() below.
   These values are the same ones index.css sets on
   :root[data-theme="dark"], because a series line and the badge beside it
   have to be the same colour -- one is drawn by ECharts from this object and
   the other by CSS from the token, and there is no mechanism keeping them in
   step except this comment.

   Contrast figures are against the dark page (#0d1117); every lifted value
   clears 4.5:1, and each one holds its light-mode hue rather than swapping
   for a nearby colour, so a series keeps its identity across the toggle.

     nsa           2.5:1 -> 6.0:1   hue 269 held
     pfc           3.7:1 -> 6.8:1   hue 209 held
     rust          3.7:1 -> 5.3:1   hue  22 held
     magenta       2.3:1 -> 5.5:1   hue 293 held
     magentaLight  (stepped with magenta to keep the pair separable)

   bgfc is deliberately absent: the orange already measures 6.3:1 on the dark
   page, so it is the same colour in both themes.                        */
export const CHART_COLORS_DARK = {
  nsa:          '#ac7cde',
  pfc:          '#5aa0e2',
  rust:         '#ea580c',
  magenta:      '#d946ef',
  magentaLight: '#f0abfc',
  // 3.5:1 on the dark page as #0e7490 -- lifted with teal so the deliberately
  // adjacent pair stays a pair rather than collapsing into one colour.
  cyan:         '#06b6d4',
};

/**
 * The palette for the theme currently on the document.
 *
 * Prefer this over importing CHART_COLORS directly in anything that draws a
 * chart. CHART_COLORS stays exported as the light palette so that existing
 * imports keep working, but a component reading it gets light colours on a
 * dark page.
 *
 * Returns a fresh object each call, so a component using it should compute
 * inside a useMemo keyed on useIsDark() rather than at module scope.
 *
 * @param {boolean} isDark - defaults to the theme on the document
 */
export function chartColors(isDark = themeIsDark()) {
  return isDark
    ? { ...CHART_COLORS_LIGHT, ...CHART_COLORS_DARK }
    : { ...CHART_COLORS_LIGHT };
}

/**
 * The palette, as a map whose every value resolves for the theme on the
 * document at the moment it is read.
 *
 * This is the name the dashboards already import, and it behaves as it always
 * did for the colours that do not change between themes. The difference is that
 * the five that do -- nsa, pfc, rust, magenta, magentaLight -- now answer with
 * the dark value on a dark page instead of quietly staying light.
 *
 * It is getters rather than a plain object because a dashboard typically writes
 * `const LOC = { NSA: CHART_COLORS.nsa }` at module scope, and a module scope
 * runs once, on a lazily-loaded page at whatever moment the user first opened
 * it. A getter cannot be captured that way.
 *
 * Spreading it (`{ ...CHART_COLORS }`) resolves every value against the theme
 * on at that instant, which is what chartColors() does deliberately and what a
 * module-scope copy should not do accidentally.
 */
export const CHART_COLORS = Object.defineProperties({}, Object.fromEntries(
  Object.keys(CHART_COLORS_LIGHT).map((key) => [key, {
    get: () => (themeIsDark() && key in CHART_COLORS_DARK
      ? CHART_COLORS_DARK[key]
      : CHART_COLORS_LIGHT[key]),
    enumerable: true,
  }])
));

/**
 * The three base colours, keyed by location code: NSA purple, PFC blue and
 * BGFC orange.
 *
 * This is the single definition of that rule. Several dashboards used to
 * repeat the three hex values in a local map of their own, which is how two
 * of them drifted -- one drew BGFC teal and PFC red, another used a different
 * orange, purple and blue from everyone else. A page needing these colours
 * should call this rather than write them down again.
 */
export function locationColors(isDark = themeIsDark()) {
  const c = chartColors(isDark);
  return { NSA: c.nsa, BGFC: c.bgfc, PFC: c.pfc };
}

/**
 * The same three colours as a ready-made map, resolved on each property access
 * so that it follows the toggle without the reader having to thread a flag
 * through.
 *
 * Import it in place of a local `const LOC_COLORS = { ... }`; every existing
 * `LOC_COLORS[code]` lookup keeps working, and spreading or Object.entries on
 * it behaves normally because the getters are enumerable.
 *
 * The usual caveat applies to anything drawn on a canvas: the value is current
 * whenever it is read, but a chart still needs its component to re-render for
 * the new option to be handed to ECharts. useIsDark() does that.
 */
export const LOCATION_COLORS = {
  get NSA()  { return chartColors().nsa; },
  get BGFC() { return chartColors().bgfc; },
  get PFC()  { return chartColors().pfc; },
};

/* ------------------------------------------------------------------
   The measure palette: a categorical set for series that are NOT bases.

   Training Timelines draws one chart per base with a bar per measure in each
   term. Inside such a chart nothing is a base, so the series need hues of
   their own -- and they cannot be the base hues, because two sections higher
   on the same page orange means BGFC, purple means NSA and blue means PFC,
   and a reader who has just learned that will carry it down the page.

   WHY FOUR AND NOT EIGHT. Four is not a taste; it is what the palette
   validator returns. With the three base families spoken for, the usable hue
   space is red, gold, green-teal and magenta, and a set has to clear five
   checks in BOTH themes at once -- lightness band, chroma floor, 3:1 against
   that theme's surface, every pair separable under protanopia and deuteranopia,
   and every slot far enough from that theme's three base colours. Searched
   over the whole in-gamut space at 2-degree hue steps, four slots clear it
   with room (worst pair 17.7 normal, 9.4 CVD, against floors of 15 and 8);
   five collapse to 12.5 and 4.9, which is two colours a reader cannot tell
   apart. So the page pairs these four with a hatch -- see HATCH in
   CourseCompletion.js -- for eight distinguishable series rather than pushing
   colour past what it can carry.

   HOW FAR FROM A BASE IS FAR ENOUGH. The bar is the one the page already sets
   rather than the validator's floor: NSA's purple and PFC's blue measure 14.8
   normal and 7.4 CVD from each other and ship on nine dashboards anyway,
   because they are shared. The closest approach here is the rose against
   BGFC's orange at 15.0 and 8.0 -- no closer than what the page already asks
   a reader to separate, and every other pairing is 20 or better.

   Hue is HELD across the toggle -- 14, 88, 176, 326 in both themes -- so a
   measure keeps its identity when the page flips; only lightness and chroma
   move, to clear the dark band and the 3:1 against #0d1117.

     light            dark             contrast (own surface)
     #f0355f rose     #f33962          3.90  ->  5.02
     #785c00 olive    #7b5f06          6.31  ->  3.14
     #02927b teal     #0d957e          3.89  ->  5.06
     #e308ed magenta  #c116c9          3.80  ->  3.81                     */
const MEASURE_COLORS_LIGHT = ['#f0355f', '#785c00', '#02927b', '#e308ed'];
const MEASURE_COLORS_DARK  = ['#f33962', '#7b5f06', '#0d957e', '#c116c9'];

/**
 * The categorical series palette, in order, for the theme on the document.
 *
 * An ARRAY rather than a named map, because these slots have no meaning: slot
 * 0 is whichever measure is first in the display order, and it changes when
 * somebody reorders them. Anything that wants a colour to MEAN something --
 * a base, a status, a phase -- wants chartColors() instead.
 *
 * Returns a fresh array each call, so compute inside a useMemo keyed on
 * useIsDark() rather than at module scope.
 *
 * @param {boolean} isDark - defaults to the theme on the document
 */
export function measureColors(isDark = themeIsDark()) {
  return isDark ? [...MEASURE_COLORS_DARK] : [...MEASURE_COLORS_LIGHT];
}

/**
 * One marker shape per base, for any chart that draws two of them as lines.
 *
 * This is the mitigation the note on `pfc` above asks for, kept here rather
 * than written out per page: NSA's purple and PFC's blue measure dE 14.8 for
 * normal vision and 7.4 under deuteranopia, so a chart holding both needs a
 * channel besides colour. Maintenance Throughput had the only copy of this map
 * and the Training Hours trend charts need the same three shapes -- a second
 * copy is how the colours themselves drifted before they were hoisted.
 *
 * Shapes, not an arbitrary order: a round dot, a three-sided mark and a
 * four-sided one stay apart at 6px, where circle-against-diamond does not.
 */
export const LOCATION_SYMBOLS = { NSA: "circle", BGFC: "triangle", PFC: "diamond" };

/**
 * Returns a base ECharts option fragment that applies FOX theming.
 * Merge this into your chart's `option` object:
 *
 *   const base = echartsBase(isDark);
 *   const option = {
 *     ...base,
 *     series: [...],
 *     xAxis: { ...base.xAxis, data: [...] },
 *   };
 *
 * @param {boolean} isDark - defaults to the theme currently on the document.
 *   Pass it explicitly only to force one; a call site that wants to follow the
 *   toggle should omit it and re-run when useIsDark() changes, because this
 *   returns a plain object that cannot update itself afterwards.
 */
export function echartsBase(isDark = themeIsDark()) {
  // Each pair is the light and dark value of a token in index.css, written out
  // rather than read with cssVar() so that this stays a pure function: a caller
  // may ask for the theme the document is *not* currently in, and a computed
  // style would answer for the document instead of the argument.
  //
  //   text --text2 | faint --text3 | border --border
  //   ttBg --bg2   | ttBdr --border2 | ttText --text
  //
  // split has no token; it is a deliberately fainter hairline than --border.
  const text   = isDark ? '#8b949e' : '#5a6070';
  // Was #484f58, which measured 2.3:1 on the dark page -- too faint for an
  // axis label to be read at 11px. --text3's dark value clears 4:1.
  const faint  = isDark ? '#6e7681' : '#9ba3af';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const split  = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const ttBg   = isDark ? '#161b22' : '#ffffff';
  const ttBdr  = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)';
  const ttText = isDark ? '#e6edf3' : '#1a1d23';

  return {
    backgroundColor: 'transparent',

    textStyle: {
      color: text,
      fontFamily: "'Inter', system-ui, sans-serif",
    },

    tooltip: {
      trigger: 'axis',
      backgroundColor: ttBg,
      borderColor: ttBdr,
      borderWidth: 0.5,
      textStyle: {
        color: ttText,
        fontSize: 12,
        fontFamily: "'Inter', system-ui, sans-serif",
      },
      axisPointer: {
        lineStyle: { color: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' },
      },
    },

    legend: {
      textStyle: { color: text, fontSize: 11 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
    },

    xAxis: {
      axisLine:  { lineStyle: { color: border } },
      axisTick:  { show: false },
      axisLabel: { color: faint, fontSize: 11 },
      splitLine: { show: false },
    },

    yAxis: {
      axisLine:  { show: false },
      axisTick:  { show: false },
      axisLabel: { color: faint, fontSize: 11 },
      splitLine: { lineStyle: { color: split, type: 'dashed' } },
    },

    grid: {
      top: 12,
      right: 16,
      bottom: 32,
      left: 48,
      containLabel: false,
    },
  };
}

/**
 * Standard series defaults — spread these into each series object.
 * Example:
 *   series: [{ ...lineDefaults, name: 'BGFC', data: [...], itemStyle: { color: CHART_COLORS.bgfc } }]
 */
export const lineDefaults = {
  type: 'line',
  smooth: true,
  showSymbol: false,
  lineStyle: { width: 2 },
  emphasis: { focus: 'series' },
};

export const barDefaults = {
  type: 'bar',
  barMaxWidth: 32,
  emphasis: { focus: 'series' },
  itemStyle: { borderRadius: [3, 3, 0, 0] },
};

/**
 * Whether charts should draw dark right now.
 *
 * Kept as its own export because several dashboards already call it. It is a
 * plain read, not a subscription -- a component that has to re-render on the
 * toggle wants useIsDark() from ./theme instead.
 */
export function getIsDark() {
  return themeIsDark();
}
